import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Building2, PenTool, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { COUNTRIES } from "../lib/countries";
import PayoutPolicyDialog from "../components/PayoutPolicyDialog";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import logoAuth from "../assets/brand/logo-auth.png";

const RoleToggle = ({ role, setRole }) => {
  const { t } = useTranslation();
  const options = [
    { value: "lab", label: t("roles.lab"), icon: Building2 },
    { value: "designer", label: t("roles.designer"), icon: PenTool },
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          data-testid={`role-${o.value}`}
          onClick={() => setRole(o.value)}
          className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm font-medium transition-colors ${
            role === o.value
              ? "border-brand bg-brand/5 text-brand"
              : "border-border text-muted-foreground hover:border-slate-300"
          }`}
        >
          <o.icon className="h-5 w-5" />
          {o.label}
        </button>
      ))}
    </div>
  );
};

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);

// Maps raw Firebase/typed errors to a user-facing i18n key (never show raw codes).
function authErrorKey(err) {
  const code = err?.code || "";
  switch (code) {
    case "auth/email-already-in-use": return "authErrors.emailInUse";
    case "profile-setup-failed": return "authErrors.profileSetupFailed";
    case "timeout": return "authErrors.timeout";
    case "auth/weak-password": return "authErrors.weakPassword";
    case "auth/invalid-email": return "authErrors.invalidEmail";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found": return "authErrors.invalidCredentials";
    case "auth/too-many-requests": return "authErrors.tooManyRequests";
    case "auth/network-request-failed": return "authErrors.network";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request": return "authErrors.popupClosed";
    default: return "authErrors.generic";
  }
}
// Errors where offering "Log in" is the right next step (account already exists).
const OFFER_LOGIN = new Set(["auth/email-already-in-use", "profile-setup-failed"]);

export default function Auth() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, register, googleSignIn, resetPassword } = useAuth();

  const [tab, setTab] = useState("login");
  const [role, setRole] = useState("lab");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState({ tos: false, privacy: false, confidentiality: false });
  const [payoutOk, setPayoutOk] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [notice, setNotice] = useState(null); // { msgKey, offerLogin }
  const [showReset, setShowReset] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  // Extended profile fields (registration).
  const [f, setF] = useState({
    accountType: "", accountTypeOther: "", name: "", contactName: "", phone: "",
    country: "", street: "", city: "", state: "", postalCode: "",
  });
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const allAgreed = agree.tos && agree.privacy && agree.confidentiality;
  const payoutRequired = role === "designer";
  const canRegister = allAgreed && (!payoutRequired || payoutOk);

  const goTo = (r) => navigate(r === "designer" ? "/designer" : "/lab");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const p = await login(email, password);
      toast.success(t("auth.loginSuccess"));
      goTo(p?.role);
    } catch (err) {
      const key = authErrorKey(err);
      setNotice({ msgKey: key, offerLogin: false });
      toast.error(t(key));
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (busy) return; // prevent accidental double submit
    if (!allAgreed) { toast.error(t("agreements.mustAccept")); return; }
    if (payoutRequired && !payoutOk) { toast.error(t("payoutPolicy.mustAccept")); return; }
    if (role === "lab") {
      if (!f.accountType) { toast.error(t("register.accountTypeRequired")); return; }
      if (f.accountType === "Other" && !f.accountTypeOther.trim()) { toast.error(t("register.specifyRequired")); return; }
    }
    setBusy(true);
    setNotice(null);
    try {
      const extra = {
        accountType: role === "lab" ? f.accountType : "designer",
        accountTypeOther: role === "lab" ? f.accountTypeOther.trim() : "",
        contactName: role === "lab" ? f.contactName : "",
        phone: f.phone,
        address: { street: f.street, city: f.city, state: f.state, postalCode: f.postalCode, country: f.country },
      };
      const p = await register(email, password, f.name, role, agree, extra);
      toast.success(t("auth.registerSuccess"));
      goTo(p?.role);
    } catch (err) {
      const key = authErrorKey(err);
      const offerLogin = OFFER_LOGIN.has(err?.code);
      setNotice({ msgKey: key, offerLogin });
      toast.error(t(key));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    if (busy) return;
    if (tab === "register" && !canRegister) {
      toast.error(payoutRequired && !payoutOk ? t("payoutPolicy.mustAccept") : t("agreements.mustAccept"));
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const p = await googleSignIn(role, agree);
      toast.success(t("auth.loginSuccess"));
      goTo(p?.role);
    } catch (err) {
      if (err?.code === "auth/popup-closed-by-user" || err?.code === "auth/cancelled-popup-request") { return; }
      const key = authErrorKey(err);
      setNotice({ msgKey: key, offerLogin: false });
      toast.error(t(key));
    } finally {
      setBusy(false);
    }
  };

  const goToLogin = () => { setNotice(null); setTab("login"); setPassword(""); };

  const handleReset = async () => {
    if (resetBusy) return;
    if (!email || !email.includes("@")) { toast.error(t("authErrors.invalidEmail")); return; }
    setResetBusy(true);
    try {
      await resetPassword(email);
      toast.success(t("resetPassword.sent"));
      setShowReset(false);
    } catch (err) {
      const key = authErrorKey(err);
      toast.error(t(key));
    } finally {
      setResetBusy(false);
    }
  };

  const CountrySelect = () => (
    <Select value={f.country} onValueChange={(v) => set("country", v)}>
      <SelectTrigger data-testid="register-country"><SelectValue placeholder={t("register.selectCountry")} /></SelectTrigger>
      <SelectContent className="max-h-64">
        {COUNTRIES.map((c) => (
          <SelectItem key={c} value={c} data-testid={`country-${c}`}>{c}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-4 flex justify-end">
          <LanguageSwitcher />
        </div>
        <div className="mb-8 text-center">
          <img
            src={logoAuth}
            alt="DentalCAD Connect"
            className="mx-auto mb-4 h-auto w-full max-w-[260px]"
            data-testid="auth-logo"
          />
          <h1 className="font-heading text-2xl font-bold tracking-tight">{t("auth.welcome")}</h1>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="tab-login">{t("auth.loginCta")}</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">{t("auth.registerCta")}</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <Field id="l-email" label={t("auth.email")}>
                  <Input id="l-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
                </Field>
                <Field id="l-pass" label={t("auth.password")}>
                  <Input id="l-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
                </Field>
                <Button type="submit" disabled={busy} className="w-full rounded-full bg-brand text-white hover:bg-brand/90" data-testid="login-submit">
                  {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("auth.signingIn")}</> : t("auth.loginCta")}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowReset((v) => !v)}
                    className="text-sm font-medium text-brand hover:underline"
                    data-testid="login-forgot-password-link"
                  >
                    {t("resetPassword.forgot")}
                  </button>
                </div>
                {showReset && (
                  <div className="space-y-2.5 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="reset-password-panel">
                    <p className="text-xs text-muted-foreground">{t("resetPassword.intro")}</p>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("auth.email")}
                      data-testid="reset-password-email"
                    />
                    <Button
                      type="button"
                      onClick={handleReset}
                      disabled={resetBusy}
                      className="w-full rounded-full bg-brand text-white hover:bg-brand/90"
                      data-testid="reset-password-submit"
                    >
                      {resetBusy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("resetPassword.sending")}</> : t("resetPassword.send")}
                    </Button>
                  </div>
                )}
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleRegister} className="space-y-4">
                <Field label={t("auth.chooseRole")}>
                  <RoleToggle role={role} setRole={setRole} />
                </Field>

                {/* Lab / Clinic / Dentist account type */}
                {role === "lab" && (
                  <>
                    <Field id="r-acctype" label={t("register.accountType")}>
                      <Select value={f.accountType} onValueChange={(v) => set("accountType", v)}>
                        <SelectTrigger data-testid="register-account-type"><SelectValue placeholder={t("register.selectAccountType")} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Dental Laboratory" data-testid="acctype-lab">{t("accountTypes.lab")}</SelectItem>
                          <SelectItem value="Dental Clinic" data-testid="acctype-clinic">{t("accountTypes.clinic")}</SelectItem>
                          <SelectItem value="Independent Dentist" data-testid="acctype-dentist">{t("accountTypes.dentist")}</SelectItem>
                          <SelectItem value="Other" data-testid="acctype-other">{t("accountTypes.other")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    {f.accountType === "Other" && (
                      <Field id="r-acctype-other" label={t("register.specify")}>
                        <Input id="r-acctype-other" required value={f.accountTypeOther} onChange={(e) => set("accountTypeOther", e.target.value)} data-testid="register-account-type-other" />
                      </Field>
                    )}
                    <Field id="r-name" label={t("register.businessName")}>
                      <Input id="r-name" required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
                    </Field>
                    <Field id="r-contact" label={t("register.contactPerson")}>
                      <Input id="r-contact" required value={f.contactName} onChange={(e) => set("contactName", e.target.value)} data-testid="register-contact-name" />
                    </Field>
                  </>
                )}

                {/* Designer full name */}
                {role === "designer" && (
                  <Field id="r-name" label={t("register.fullName")}>
                    <Input id="r-name" required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
                  </Field>
                )}

                <Field id="r-email" label={t("auth.email")}>
                  <Input id="r-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" />
                </Field>
                <Field id="r-phone" label={t("register.phone")}>
                  <Input id="r-phone" type="tel" required value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="register-phone" />
                </Field>
                <Field id="r-country" label={t("register.country")}>
                  <CountrySelect />
                </Field>
                <Field id="r-street" label={t("register.street")}>
                  <Input id="r-street" required value={f.street} onChange={(e) => set("street", e.target.value)} data-testid="register-street" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field id="r-city" label={t("register.city")}>
                    <Input id="r-city" required value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="register-city" />
                  </Field>
                  <Field id="r-state" label={t("register.state")}>
                    <Input id="r-state" required value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="register-state" />
                  </Field>
                </div>
                <Field id="r-postal" label={t("register.postal")}>
                  <Input id="r-postal" required value={f.postalCode} onChange={(e) => set("postalCode", e.target.value)} data-testid="register-postal" />
                </Field>
                <Field id="r-pass" label={t("auth.password")}>
                  <Input id="r-pass" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
                </Field>

                {/* Legal agreements */}
                <div className="space-y-2.5 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="agreements-block">
                  <p className="text-xs text-muted-foreground">{role === "designer" ? t("agreements.designerIntro") : t("agreements.labIntro")}</p>
                  {[
                    ["tos", t("agreements.acceptTos")],
                    ["privacy", t("agreements.acceptPrivacy")],
                    ["confidentiality", t("agreements.acceptConfidentiality")],
                  ].map(([k, label]) => (
                    <label key={k} className="flex cursor-pointer items-start gap-2 text-sm" data-testid={`agree-${k}`}>
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300"
                        checked={agree[k]}
                        onChange={(e) => setAgree((a) => ({ ...a, [k]: e.target.checked }))}
                        data-testid={`agree-${k}-checkbox`}
                      />
                      <span>
                        {label}{" "}
                        <a href="#" onClick={(e) => e.preventDefault()} className="text-brand underline-offset-2 hover:underline" title={t("agreements.draftTag")}>
                          ({t("agreements.draftTag")})
                        </a>
                      </span>
                    </label>
                  ))}
                </div>

                {/* Designer payout policy disclosure */}
                {role === "designer" && (
                  <div className="space-y-2.5 rounded-lg border border-brand/30 bg-brand/5 p-3" data-testid="payout-policy-block">
                    <p className="text-xs font-semibold text-foreground">{t("payoutPolicy.title")}</p>
                    <p className="text-xs text-muted-foreground">{t("payoutPolicy.shortIntro")}</p>
                    <button type="button" onClick={() => setPolicyOpen(true)} className="text-xs font-medium text-brand hover:underline" data-testid="view-payout-policy-register">
                      {t("payoutPolicy.view")}
                    </button>
                    <label className="flex cursor-pointer items-start gap-2 text-sm" data-testid="agree-payout">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300"
                        checked={payoutOk}
                        onChange={(e) => setPayoutOk(e.target.checked)}
                        data-testid="agree-payout-checkbox"
                      />
                      <span>{t("payoutPolicy.accept")}</span>
                    </label>
                  </div>
                )}

                <Button type="submit" disabled={busy || !canRegister} className="w-full rounded-full bg-brand text-white hover:bg-brand/90" data-testid="register-submit">
                  {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> {t("auth.creatingAccount")}</> : t("auth.registerCta")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {notice && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="auth-notice">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <p data-testid="auth-notice-message">{t(notice.msgKey)}</p>
                {notice.offerLogin && (
                  <button type="button" onClick={goToLogin} className="mt-1.5 font-semibold text-brand hover:underline" data-testid="auth-notice-login">
                    {t("auth.loginCta")}
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> {t("auth.or")} <div className="h-px flex-1 bg-border" />
          </div>

          <Button variant="outline" className="w-full rounded-full gap-2" onClick={handleGoogle} disabled={busy || (tab === "register" && !canRegister)} data-testid="google-signin">
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
              <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z" />
            </svg>
            {t("auth.googleCta")}
          </Button>
        </div>
      </div>

      <PayoutPolicyDialog open={policyOpen} onOpenChange={setPolicyOpen} mode="view" />
    </div>
  );
}
